import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Plus, Pencil, Trash2, Package, ShoppingBag, TrendingUp, AlertTriangle, Download, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';

const EMPTY = { name: '', category: '', brand: 'Alibaba Supplier', cost_price: '', price: '', oldPrice: '', stock: '', image: '', description: '', source_url: '', active: true };
const euro = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`;

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, p, o, s] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/products`, { withCredentials: true }),
        axios.get(`${API}/admin/orders`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setCategories(c.data);
      setProducts(p.data);
      setOrders(o.data);
      setStats(s.data);
    } catch {
      toast({ title: 'Erreur de chargement', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (user === null) return; // still checking
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    loadAll();
  }, [user, navigate, loadAll]);

  if (user === null || loading) {
    return <div className="flex justify-center py-40 text-[#00847A]"><Loader2 size={40} className="animate-spin" /></div>;
  }
  if (!user || user.role !== 'admin') return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd = () => { setEditingId(null); setForm({ ...EMPTY, category: categories[0]?.id || '' }); setImportUrl(''); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, category: p.category, brand: p.brand, cost_price: p.cost_price ?? '', price: p.price,
      oldPrice: p.oldPrice ?? '', stock: p.stock, image: p.image, description: p.description || '',
      source_url: p.source_url || '', active: p.active,
    });
    setImportUrl('');
    setDialogOpen(true);
  };

  const importAlibaba = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/admin/import/alibaba`, { url: importUrl.trim() }, { withCredentials: true });
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        image: data.image || f.image,
        description: data.description || f.description,
        source_url: importUrl.trim(),
        cost_price: data.price != null ? data.price : f.cost_price,
      }));
      toast({ title: data.note ? 'Import partiel' : 'Import réussi', description: data.note || 'Infos pré-remplies. Vérifiez puis enregistrez.' });
    } catch {
      toast({ title: "Échec de l'import", description: 'Remplissez le formulaire manuellement.', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.category || !form.price || !form.image) {
      toast({ title: 'Champs requis manquants', description: 'Nom, catégorie, prix de vente et image sont obligatoires.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name, category: form.category, brand: form.brand || 'Alibaba Supplier',
      cost_price: parseFloat(form.cost_price) || 0, price: parseFloat(form.price),
      oldPrice: form.oldPrice ? parseFloat(form.oldPrice) : null, stock: parseInt(form.stock) || 0,
      image: form.image, description: form.description, source_url: form.source_url, active: form.active,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      toast({ title: editingId ? 'Produit mis à jour' : 'Produit ajouté' });
      setDialogOpen(false);
      loadAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Enregistrement impossible', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`, { withCredentials: true });
      toast({ title: 'Produit supprimé' });
      loadAll();
    } catch {
      toast({ title: 'Suppression impossible', variant: 'destructive' });
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, { active: !p.active }, { withCredentials: true });
      loadAll();
    } catch {
      toast({ title: 'Action impossible', variant: 'destructive' });
    }
  };

  const margin = (p) => (p.cost_price != null ? euro(p.price - p.cost_price) : '—');

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black text-[#262626]">🏬 Entrepôt WorldMarket</h1>
          <p className="text-sm text-[#595959]">Gérez vos produits, votre stock et vos commandes</p>
        </div>
        <Button onClick={openAdd} data-testid="add-product-btn" className="bg-[#00847A] hover:bg-[#006B63] text-white rounded-full h-11 px-5 font-semibold">
          <Plus size={18} className="mr-1" /> Ajouter un produit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Package} label="Produits" value={stats?.total_products} sub={`${stats?.active_products} actifs`} />
        <StatCard icon={AlertTriangle} label="En rupture" value={stats?.out_of_stock} sub="à réapprovisionner" color="text-amber-600" />
        <StatCard icon={ShoppingBag} label="Commandes payées" value={stats?.orders_paid} sub="total" />
        <StatCard icon={TrendingUp} label="Chiffre d'affaires" value={euro(stats?.revenue || 0)} sub="encaissé" color="text-[#00847A]" raw />
      </div>

      <Tabs defaultValue="products">
        <TabsList className="bg-neutral-100">
          <TabsTrigger value="products" className="data-[state=active]:bg-white data-[state=active]:text-[#00847A]">Produits ({products.length})</TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-white data-[state=active]:text-[#00847A]">Commandes ({orders.length})</TabsTrigger>
        </TabsList>

        {/* Products */}
        <TabsContent value="products" className="pt-4">
          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-[#595959]">
                <tr>
                  <th className="p-3">Produit</th>
                  <th className="p-3">Catégorie</th>
                  <th className="p-3">Achat</th>
                  <th className="p-3">Vente</th>
                  <th className="p-3">Marge</th>
                  <th className="p-3">Stock</th>
                  <th className="p-3">Actif</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="admin-products-table">
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt={p.name} className="w-10 h-10 rounded object-cover" />
                        <span className="font-medium text-[#262626] line-clamp-1 max-w-[220px]">{p.name}</span>
                      </div>
                    </td>
                    <td className="p-3 text-[#595959]">{p.category}</td>
                    <td className="p-3 text-[#595959]">{p.cost_price != null ? euro(p.cost_price) : '—'}</td>
                    <td className="p-3 font-semibold text-[#262626]">{euro(p.price)}</td>
                    <td className="p-3 text-[#00847A] font-semibold">{margin(p)}</td>
                    <td className="p-3">{p.stock > 0 ? <span className="text-[#262626]">{p.stock}</span> : <Badge variant="destructive">Rupture</Badge>}</td>
                    <td className="p-3"><Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-${p.id}`} /></td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 hover:bg-neutral-100 rounded-lg text-[#00847A]"><Pencil size={16} /></button>
                        <button onClick={() => remove(p.id)} data-testid={`delete-${p.id}`} className="p-2 hover:bg-neutral-100 rounded-lg text-[#FF4D4D]"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#595959]">Aucun produit. Cliquez sur « Ajouter un produit ».</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders" className="pt-4">
          <div className="overflow-x-auto border border-neutral-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-[#595959]">
                <tr><th className="p-3">Date</th><th className="p-3">Client</th><th className="p-3">Articles</th><th className="p-3">Montant</th><th className="p-3">Statut</th></tr>
              </thead>
              <tbody data-testid="admin-orders-table">
                {orders.map((o, i) => (
                  <tr key={i} className="border-t border-neutral-100">
                    <td className="p-3 text-[#595959]">{(o.created_at || '').slice(0, 10)}</td>
                    <td className="p-3 text-[#262626]">{o.shipping?.full_name || o.shipping?.email || '—'}</td>
                    <td className="p-3 text-[#595959]">{(o.items || []).reduce((a, it) => a + (it.quantity || 0), 0)}</td>
                    <td className="p-3 font-semibold">{euro((o.amount || 0) / 100)}</td>
                    <td className="p-3">
                      <Badge className={o.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-600'}>
                        {o.payment_status === 'paid' ? 'Payée' : (o.payment_status || 'en attente')}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#595959]">Aucune commande pour l'instant.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? 'Modifier le produit' : 'Ajouter un produit'}</DialogTitle></DialogHeader>

          {!editingId && (
            <div className="bg-[#F0F9F8] border border-[#00847A]/20 rounded-lg p-3">
              <Label className="text-xs text-[#00847A]">Importer depuis un lien Alibaba / AliExpress (best-effort)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} data-testid="import-url-input" placeholder="https://www.alibaba.com/product-detail/..." className="flex-1" />
                <Button onClick={importAlibaba} disabled={importing} data-testid="import-btn" variant="outline" className="border-[#00847A] text-[#00847A] whitespace-nowrap">
                  {importing ? <Loader2 size={16} className="animate-spin" /> : <><Download size={16} className="mr-1" /> Importer</>}
                </Button>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nom du produit *</Label>
              <Input value={form.name} onChange={set('name')} data-testid="form-name" className="mt-1" />
            </div>
            <div>
              <Label>Catégorie *</Label>
              <select value={form.category} onChange={set('category')} data-testid="form-category" className="mt-1 w-full border border-neutral-300 rounded-md h-10 px-3 text-sm">
                <option value="">— choisir —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Fournisseur</Label>
              <Input value={form.brand} onChange={set('brand')} data-testid="form-brand" className="mt-1" />
            </div>
            <div>
              <Label>Prix d'achat (Alibaba) €</Label>
              <Input type="number" step="0.01" value={form.cost_price} onChange={set('cost_price')} data-testid="form-cost" className="mt-1" />
            </div>
            <div>
              <Label>Prix de vente €  *</Label>
              <Input type="number" step="0.01" value={form.price} onChange={set('price')} data-testid="form-price" className="mt-1" />
            </div>
            <div>
              <Label>Prix barré € (optionnel)</Label>
              <Input type="number" step="0.01" value={form.oldPrice} onChange={set('oldPrice')} data-testid="form-oldprice" className="mt-1" />
            </div>
            <div>
              <Label>Stock</Label>
              <Input type="number" value={form.stock} onChange={set('stock')} data-testid="form-stock" className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>URL de l'image *</Label>
              <Input value={form.image} onChange={set('image')} data-testid="form-image" className="mt-1" placeholder="https://..." />
              {form.image && <img src={form.image} alt="" className="mt-2 w-20 h-20 rounded object-cover border" />}
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={set('description')} data-testid="form-description" className="mt-1" rows={3} />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="form-active" />
              <Label>Visible dans la boutique</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}><X size={16} className="mr-1" /> Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-product-btn" className="bg-[#00847A] hover:bg-[#006B63] text-white">
              {saving ? <Loader2 size={16} className="animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-[#262626]', raw }) => (
  <div className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="flex items-center gap-2 text-[#595959] text-sm"><Icon size={16} /> {label}</div>
    <div className={`text-2xl font-black mt-1 ${color}`}>{raw ? value : (value ?? 0)}</div>
    <div className="text-xs text-[#595959]">{sub}</div>
  </div>
);

export default Admin;
